import IBoardTypeDescriptor from './IBoardTypeDescriptor';
import ICountConfigTypeDescriptor from './ICountConfigTypeDescriptor';
import IEdgeTypeDescriptor from './IEdgeTypeDescriptor';
import IUnitsTypeDescriptor from './IUnitsTypeDescriptor';
import { IRelationsDescriptor } from '../../../configurator/cards/backPassingRelations';

/**
 * EGY láthatósági feltétel: egy MÁSIK paraméter adott mezője az `in` listában szereplő értékek egyikét
 * veszi fel. A `visibleWhen` egyetlen feltételt vagy feltétel-LISTÁT is elfogadhat; lista esetén MIND
 * teljesülnie kell (ÉS-kapcsolat). Erre a láncolt függéseknél van szükség — pl. a polctartó furatszáma
 * csak akkor látszik, ha a polc áthelyezhető ÉS a polctartó "több furat".
 */
export interface TParamVisibility {
    /** A másik paraméter neve (uc_style_parameters.style_parameter_name). */
    paramName: string;
    /** A wrapper property-kulcsa a másik paraméternél (pl. "Railing"). */
    property: string;
    /** Opcionális további kulcs, ha a property értéke objektum (pl. "RailingType"). */
    field?: string;
    /** Látható, ha a feloldott érték ezek EGYIKE. Elhagyható, ha csak `notIn` szűr. */
    in?: string[];
    /**
     * Látható, ha a feloldott érték EGYIKE SEM ezeknek. A kizárás gyakrabban a helyes alak, mint a
     * felsorolás: "a front dekorja mindig kell, KIVÉVE ha nincs front" — így egy új front-típus
     * automatikusan bekerül, nem kell hozzányúlni a feltételhez.
     * Mindkettő megadható; ilyenkor MIND a kettőnek teljesülnie kell.
     */
    notIn?: string[];
}

/**
 * Interface for parameter type descriptors. These affect the behavior of the parameter card appearance on the Configurator.
 */
interface IParamTypeDescriptor {
    label?: string;
    tooltip?: string;
    default?: string | number;
    placeholder?: string;
    sortIndex?: number;
    editable?: boolean;
    readOnly?: boolean;
    hidden?: boolean;
    /**
     * Optional, ADAT-VEZÉRELT feltétel: a property csak akkor jelenik meg (renderelődik) a kártyák
     * között, ha egy MÁSIK paraméter adott mezője az `in` listában szereplő értékek egyikét veszi fel.
     * Pl. a Railing-dekor csak akkor látszik, ha a Railing param típusa Single/Double
     * (lásd PropertyContainerCard, ahol ez kiértékelődik).
     */
    visibleWhen?: TParamVisibility | TParamVisibility[];
    /**
     * Opcionális: a paraméter KIMENETI ÉRTÉKE egy MÁSIK paraméterből származik. A tárolt saját értéket
     * ilyenkor nem küldjük ki — a `cases` a hivatkozott paraméter értékéből képez kimeneti értéket.
     *
     * MIÉRT NEM `effects`: a mellékhatás csak VÁLTOZÁSKOR fut, betöltéskor nem, tehát egy származtatott
     * érték idővel elcsúszhat a forrásától. Ez itt MINDEN kimenet-generáláskor újraszámolódik, tehát
     * elcsúszni nem tud.
     *
     * A `cases` értékében `{Paraméter.Property}` alakú helyettesítő állhat — ugyanaz a konvenció, mint
     * az elosztás `emitEntries` sablonjában:
     *
     *   "emitFrom": { "paramName": "GeneralShelfSupport", "property": "ShelfSupport",
     *                 "cases": { "Single": "None", "Series": "Continuous",
     *                            "Multiple": "Line{GeneralShelfDrillCount.ShelfDrillCount}" } }
     *
     * Nem szereplő eset → a paraméter a SAJÁT tárolt értékén megy ki (a szabály nem "nyeli el" az
     * ismeretlen állapotot).
     */
    emitFrom?: {
        paramName: string;
        property: string;
        field?: string;
        cases: { [value: string]: string | object };
    };
    /**
     * Opcionális: a paraméter a stylescriptbe SZÁMKÉNT megy ki, ne méret-stringként. A TÁROLÁS és a UI
     * változatlan marad (`"32mm"`), csak a kimenet lesz `32` — a mértékegység a Unity-szerződés része,
     * nem az értéké.
     *
     * Azért kell, mert a Unity oldalán ugyanannak az elemnek lehetnek `UnitValue` és `double` mezői is:
     * a polcfuratozásnál az átmérő és a mélység egységgel érkezik, a furattávolság és a visszaállások
     * viszont csupasz számként (`Could not convert string to double: 16mm`).
     */
    emitNumber?: boolean;
    /**
     * Opcionális: a paraméter UGYANAZT az értéket TÖBB ÚTVONALRA küldi ki, útvonalanként külön
     * entryben. Az `emitAs` párja: az egy értéket több KULCSRA viszi egy entryn belül, ez pedig több
     * ENTRYRE, útvonalanként.
     *
     *   "emitPaths": { "paths": ["*.DrawerZone[{i}].Left", "*.DrawerZone[{i}].Right"],
     *                  "countFrom": { "paramName": "GeneralDrawerCount", "property": "DrawerCount" } }
     *
     * A `{i}` a `countFrom`-ból feloldott darabszám szerint 0..N-1 értéket vesz fel — ugyanaz a
     * konvenció, mint az elosztás `emitEntries` sablonjában. `countFrom` nélkül az útvonalak szó
     * szerint mennek ki, egyszer.
     *
     * MIÉRT NEM `countFrom` a gyökéren: az a paramétert DARABSZÁM-VEZÉRELT LISTA-kártyává teszi
     * (N külön input + Auto kapcsoló, lásd resolveItems). Itt EGY mező van a UI-n, csak a kimenete
     * sokszorozódik — a kártya alakja nem változik.
     */
    emitPaths?: {
        /** Útvonal-sablonok; a `{i}` a sorszám helye. Objektum alakban saját `visibleWhen`-nel. */
        paths: (string | { path: string; visibleWhen?: TParamVisibility | TParamVisibility[] })[];
        /** Opcionális ismétlés-forrás. Hiányában az útvonalak egyszer, sablon-behelyettesítés nélkül mennek ki. */
        countFrom?: { paramName: string; property: string };
    };
    /**
     * Opcionális: ÁLLANDÓ kulcsok, amiket minden kimeneti entry megkap. Nem a felhasználó állítja és
     * nincs UI-ja — a Unity-szerződés része, mint a `TargetType`.
     *
     *   "emitConst": { "TopMargin": "*" }
     *
     * Az `emitPaths`-tól függetlenül működik; ha mindkettő van, minden szétszórt entry megkapja.
     */
    emitConst?: Record<string, string | number>;
    /**
     * Opcionális: az üres objektum-érték („nincs alkatrész") is kimegy, `{"Hinges": {}}` alakban — ahol
     * a Unity ebből tudja levenni a korábban felrakott elemet. Hiányában az üres érték ki sem megy.
     */
    emitEmpty?: boolean;
    /**
     * Opcionális kapu az `emitConst`-hoz: a konstansok CSAK akkor mennek ki, ha a paraméter saját
     * értéke ebben az egységben áll (pl. `"mm"`). Hiányában az `emitConst` feltétel nélküli.
     */
    emitConstWhenUnit?: string;
    /**
     * Opcionális, ADAT-VEZÉRELT MELLÉKHATÁSOK: ha EZ a paraméter megváltozik és a `when` feltétel
     * teljesül az új értékre, akkor a `set`-ben megadott MÁSIK paraméter (vagy annak egy mezője) a
     * megadott értékre áll. Pl. a keresztmerevítő bekapcsolása a "Tető kapcsolat"-ot Közéülőre állítja,
     * és fordítva. A kiértékelés a paramEffects.ts-ben van; a mellékhatások NEM kaszkádolnak (a cél
     * paraméter saját effects-e már nem fut le), így körkörös szabály-pár is biztonságos.
     */
    effects?: Array<{
        /** Feltétel az ÚJ értékre. `field` composite értéknél a mező kulcsa (pl. "RailingType"). */
        when: { field?: string; in?: string[]; notIn?: string[] };
        set: {
            /** A cél paraméter neve (uc_style_parameters.style_parameter_name). */
            paramName: string;
            /** A cél wrapper property-kulcsa (pl. "Railing"). */
            property: string;
            /** Opcionális mező-kulcs, ha a cél értéke objektum (pl. "RailingType"). */
            field?: string;
            /** A beírandó érték. */
            value: string;
            /** Ha a cél MOSTANI értéke ezek egyike, nem írjuk át (pl. bekapcsolt merevítést ne rontsunk el). */
            keepIfIn?: string[];
        };
    }>;
}

export interface ITextParamTypeDescriptor extends IParamTypeDescriptor {
    multiline?: boolean;
}

/**
 * Bool: önálló be/ki kapcsoló (pl. a fiókos zóna `DoubleFront`-ja). A `default` itt SZÁNDÉKOSAN
 * boolean — a bázis-interfészben `string | number`, mert ott méret- és enum-alapértékeket ír le.
 *
 * Az ELTÁROLT érték a DB-ben string ("true"/"false", a `style_parameter_value` varchar), a Unity felé
 * viszont IGAZI JSON boolean megy — a konverzió a kártyában van (BoolCardContent), ugyanazzal a
 * `String(v) === "true"` mintával, amit a BackPassing `Top` kapcsolója is használ.
 */
export interface IBoolParamTypeDescriptor extends Omit<IParamTypeDescriptor, "default"> {
    default?: boolean;
}

export interface IEnumParamTypeDescriptor extends IParamTypeDescriptor {
    options: { [name: string]: string };
    // Opcionális, ADAT-VEZÉRELT Relations-entry template a Unity CabinetFrame stylescripthez
    // (passing Enum-ok: Top/BottomPassingRelation). `entry` alak — az enum-érték választja ki a
    // byRelation-szabályt. Ha hiányzik, a param változatlanul (nyers enumként) megy ki.
    // Feloldás/alak: lásd configurator/cards/backPassingRelations.ts (IRelationsDescriptor.entry).
    relations?: IRelationsDescriptor;
}

export interface ICountParamTypeDescriptor extends IParamTypeDescriptor {
    config: ICountConfigTypeDescriptor;
}

/**
 * Hivatkozás egy MÁSIK paraméter mezőjére. Ugyanaz az alak, amit a `visibleWhen` is használ — egy
 * fogalom, egy alak az egész descriptor-nyelvben.
 */
export type TParamFieldRef = { paramName: string; property: string; field?: string };

export interface ISizeParamTypeDescriptor extends IParamTypeDescriptor {
    units: IUnitsTypeDescriptor;
    minFrom?: TParamFieldRef;
    maxFrom?: TParamFieldRef;
    maxFromBoard?: {
        paramName: string;
        property: string;
        field: string;
        attribute: "SheetWidth" | "SheetHeight" | "SheetThickness";
    };
    budget?: { from: TParamFieldRef; siblings?: TParamFieldRef[] };
}

/**
 * EGY AL-MEZŐ egy összetett (composite) paraméterben — a `key` mondja meg, melyik kulcs alá kerül az
 * értékben (és így a Unity felé menő JSON-ben), a `kind` pedig azt, MILYEN mező.
 *
 * A háromféle levél (méret / választós / logikai) nem esetleges: pontosan ez a három tér vissza a
 * Size-ban, a Railing szélességében, a BackPassing ráhagyásában — lásd a paramDefaults.ts fejlécét,
 * ami ugyanezt a felosztást használja az alapértékekhez (`TFieldSpec`). Ezért egy összetett paraméter
 * nem új logika, csak NEVESÍTETT LEVELEK LISTÁJA.
 *
 * `emitAs` — a mező ÁTKULCSOLÁSA a Unity-stylescripthez. A `key` mindig a SZERKESZTÉS és a TÁROLÁS
 * kulcsa (a kosár-descriptor is ezt kapja); az `emitAs` csak a kimenetre hat:
 *   hiányzik              → a mező a saját kulcsán megy ki (a szokásos eset),
 *   `[]`                  → NEM megy ki. Olyan mezőkhöz, amik a TÖBBI mezőt vezérlik, de a
 *                           3D-modellnek nincs jelentésük (pl. a fióksín típusa),
 *   `["Left","Right"]`    → az érték MINDEN felsorolt kulcson kimegy. Ahol a felhasználónak egy
 *                           döntés, a Unitynak viszont több mező (pl. egy "oldalsó ráhagyás" mező
 *                           a Left/Right kulcsokra).
 *
 * Ugyanez a DESCRIPTOR GYÖKERÉN is áll: az ott megadott `emitAs: []` az EGÉSZ paramétert kihagyja a
 * stylescriptből (pl. az "Épített fiók" kapcsoló, ami csak más mezők láthatóságát vezérli). Enélkül a
 * Unity "No StyleApplier found for StyleElement" hibát ír rá.
 */
export type TCompositeFieldDescriptor =
    | {
        key: string;
        kind: "size";
        label?: string;
        tooltip?: string;
        default?: string;
        emitAs?: string[];
        units: IUnitsTypeDescriptor;
        minFrom?: TParamFieldRef;
        maxFrom?: TParamFieldRef;
        /**
         * ÁGFÜGGŐ KORLÁT: a mező numerikus határai egy TESTVÉR mező aktuális értékétől függnek. Ugyanaz
         * az érték-alak, mint a BackPassing `byRelation`-jénél (`IRelationFieldOverride`) — a különbség
         * csak annyi, hogy ott a hivatkozott mező kódba volt írva, itt a `field` nevezi meg.
         *
         * Pl. a polctartó furat-távolsága több furatnál 16-64 mm, furatsornál viszont 32-64:
         *   "boundsBy": { "field": "ShelfSupport", "cases": { "Series": { "min": 32 } } }
         *
         * Ami egy ágnál nincs megadva, az a `units` configból marad. A szabály CSAK a határokat mozgatja,
         * értéket nem ír — ágváltáskor a beírt érték a mezőben marad, amíg a felhasználó hozzá nem nyúl
         * (ugyanúgy, ahogy a BackPassing viselkedik ma).
         */
        boundsBy?: { field: string; cases: { [value: string]: IRelationFieldOverride } };
        visibleWhen?: TCompositeFieldVisibility;
    }
    | {
        key: string;
        kind: "enum";
        label?: string;
        tooltip?: string;
        default?: string;
        emitAs?: string[];
        options: { [name: string]: string };
        visibleWhen?: TCompositeFieldVisibility;
    }
    | {
        key: string;
        kind: "bool";
        label?: string;
        tooltip?: string;
        default?: boolean;
        emitAs?: string[];
        visibleWhen?: TCompositeFieldVisibility;
    }
    /**
     * EGÉSZ SZÁM — egység nélküli darabszám (pl. a polctartó "furatok száma"). A `config` SZÁNDÉKOSAN a
     * meglévő `ICountConfigTypeDescriptor`: ugyanaz az alak, amit az önálló `Count` paraméter is használ,
     * és amit a `TFieldSpec` `number` ága már ma is ismer az alapérték-feloldáshoz.
     */
    | {
        key: string;
        kind: "number";
        label?: string;
        tooltip?: string;
        emitAs?: string[];
        config: ICountConfigTypeDescriptor;
        visibleWhen?: TCompositeFieldVisibility;
    };

/**
 * Egy al-mező feltételes láthatósága a SAJÁT composite-ján belül: akkor látszik, ha egy TESTVÉR mező
 * értéke az `in` listában szerepel. Ez a `visibleForRelations` (BackPassing) és `visibleForTypes`
 * (Railing) általánosítása — ott a hivatkozott mező kódba volt írva, itt adat.
 */
export type TCompositeFieldVisibility = { field: string; in: string[] };

/**
 * ÖSSZETETT (composite) paraméter: az értéke objektum, a mezőit a descriptor `fields` tömbje sorolja
 * fel. Bármelyik paraméter-TÍPUS descriptora lehet ilyen — a kártya-választás a descriptor ALAKJÁRA
 * néz, nem a típus nevére (lásd PropertyContainerCard), így egy új objektum-értékű típus (CabinetTilt,
 * és később QuadrantArcCutout / Rounding / ToeKick / Drilling / DrillSeries) bekötéséhez NEM kell sem
 * új komponens, sem új switch-ág, sem regiszter-sor — csak a DB-ben egy descriptor.
 *
 * A Railing és a BackPassing SZÁNDÉKOSAN marad a saját kártyáján: ott a mezők egymásra hatnak
 * (kötés-függő defaultok és korlátok, be/ki checkbox implicit `offValue`-val), ami több, mint amit a
 * mezőnkénti leírás kifejez.
 */
export interface ICompositeParamTypeDescriptor extends IParamTypeDescriptor {
    fields: TCompositeFieldDescriptor[];
    /** Mint a Railingnél: a mezők a wrapper GYÖKERÉRE kerülnek, nem a property-kulcs alá. */
    flatten?: boolean;
}

/**
 * ELOSZTÁS (distribution): egy zóna gyerekeinek magassága — ANNYI méret-mező, amennyit egy MÁSIK
 * paraméter (a darabszám) mond. Három helyen ugyanez: polcos zóna, fiókos zóna, tömbvágás lapjai.
 *
 * Az érték objektum: `{ "Auto": "true"|"false", "Heights": ["1*", "300mm", …] }`.
 *   - `Auto`  — "Ajánlott elosztás". CSAK a UI-é (`emitAs: {"Auto": []}`), a Unity felé nem megy ki.
 *               Bekapcsolva a mezők el sem jelennek, és a kimenetbe egyenletes (csupa `1*`) lista kerül.
 *   - `Heights` — VALÓDI tömb, nem vesszős string: az elemszám-változás, az egység-rögzítés és a
 *               mezőnkénti visszaállítás mind index-alapú művelet. A Unity-alak (vesszős lista vagy
 *               indexelt kulcsok) a KIMENETI úton áll elő, nem a tárolásban.
 *
 * MIÉRT NEM indexelt property (`Heights[0]`, `Heights[1]`): a `style_parameter_property_index` DB-oszlop,
 * tehát soronként fix — az elemszám viszont futásidőben, a darabszám-mezőből jön, és kosaranként más.
 */
export interface IDistributionParamTypeDescriptor extends IParamTypeDescriptor {
    /** Az "Ajánlott elosztás" kapcsoló címkéje (a bázis `label`). */
    /** Egy mező címkéje; a `{n}` helyére az 1-alapú sorszám kerül (pl. "{n}. polcos zóna magassága"). */
    itemLabel: string;
    /**
     * Az elemszámot adó MÁSIK paraméter — ugyanaz az alak, mint a `visibleWhen` / `minFrom`.
     *
     * A `plus` a hivatkozott darabszámhoz ADOTT ELTOLÁS, mert a zónák száma nem mindig egyezik az
     * elválasztók számával: n POLC n+1 polcos zónát ad (`plus: 1`), míg n fiók pontosan n fiókos
     * zónát. Azért adat és nem beégetett szabály, hogy egy új zóna-fajta ne igényeljen kódot.
     */
    countFrom: TParamFieldRef & { plus?: number };
    /** A mezők egység-készlete; az ELSŐ egység a fallback (a `*`-ot ide kell előre tenni). */
    units: IUnitsTypeDescriptor;
    /** Felső korlát az elemszámra — a tárolás (varchar 1024) miatt nem lehet végtelen. */
    maxItems?: number;
    /**
     * KERET: az elemek EGYÜTT a `from`-ban hivatkozott méretből fogyasztanak (a zóna magasságából).
     * A testvéreket itt nem kell felsorolni — az elemek egymás testvérei. Lásd paramBounds `budgetMax`.
     */
    budget?: { from: TParamFieldRef };
    /**
     * A Unity-KIMENET alakja. Az elosztás NEM egyetlen entryként megy ki: a Unity elemenként KÜLÖN
     * entryt vár, mindegyiket a saját útvonalával:
     *     {"Path": "*.DrawerZone[0]", "Height": "200mm"}
     *     {"Path": "*.DrawerZone[1]", "Height": "150mm"}
     *
     * A `path` sablonban a `{i}` helyére a ZÓNA-INDEX kerül: `start + sorszám * step`. A `step` azért
     * adat, mert zóna-fajtánként más: a fiókoknál minden zóna egy fiók (0,1,2…), a polcoknál viszont
     * minden MÁSODIK index egy Space, ezért ott 0,2,4… a helyes (a Unity így építi a hierarchiát).
     *
     * Hiányában a paraméter nem megy ki a Unitynak (a kártya és a kosár-descriptor változatlanul él) —
     * így egy zóna-fajta akkor is bekötheto a UI-ba, ha a Unity-oldali alakja még nincs eldöntve.
     */
    emitEntries?: {
        /** Útvonal-sablon, a `{i}` a zóna-index helye (pl. "*.DrawerZone[{i}]"). */
        path: string;
        /** A méret kulcsa az entryben (pl. "Height"). */
        key: string;
        /** Két egymást követő elem index-lépése. Hiányában 1. */
        step?: number;
        /** Az első elem indexe. Hiányában 0. */
        start?: number;
    };
    /** Az "Ajánlott elosztás" alapállása. Hiányában bekapcsolt (a mai viselkedés). */
    auto?: boolean;
}

export interface IDecorParamTypeDescriptor extends IParamTypeDescriptor {
    board: IBoardTypeDescriptor;
    edge: IEdgeTypeDescriptor;
}

/**
 * Egy méret-mező KÖTÉS-FÜGGŐ felülírásai. ÁLTALÁNOS mechanizmus: a `byRelation` alatt kötés-típusonként
 * a mező bármelyik beállítása felülírható — az alapértelmezett érték és a numerikus korlátok egyaránt.
 * Így egy új szabály (pl. "nútolásnál a max legyen más") CSAK ADAT, nem kódmódosítás.
 *
 * Példa (ráfekvőnél nincs ráhagyás, falcolás/nútolás viszont megkövetel 3 mm-t):
 *   "byRelation": { "Butt": { "default": "0mm" }, "Inlay": { "min": 3 }, "Groove": { "min": 3 } }
 */
export interface IRelationFieldOverride {
    /** A mező kezdőértéke ennél a kötésnél ("0mm" alak). */
    default?: string;
    min?: number;
    max?: number;
    smallStep?: number;
    largeStep?: number;
    decimals?: number;
}

/** Egy BackPassing méret-mező (Ráhagyás / Mélység) leírója. */
export interface IBackPassingSizeField {
    label?: string;
    /** Alapértelmezett érték, ha a kötéshez nincs külön megadva. */
    default?: string;
    /** Kötés-függő felülírások (érték + numerikus korlátok) — lásd IRelationFieldOverride. */
    byRelation?: { [relation: string]: IRelationFieldOverride };
    /**
     * LEGACY, csak visszafelé kompatibilitásból: a korábbi, kizárólag az alapértelmezett ÉRTÉKET
     * felülíró alak. A `byRelation[kötés].default` erősebb; ez akkor él, ha a descriptort még nem
     * migrálták (így egy régi adatú környezet viselkedése sem változik).
     */
    defaultByRelation?: { [relation: string]: string };
    /** Mely kötéseknél jelenik meg a mező. Hiányában mindig látszik. */
    visibleForRelations?: string[];
    units: IUnitsTypeDescriptor;
}

/**
 * BackPassing (Kötőelemek): composite object value {Relation, Depth, Margin, Top}.
 * Each sub-field carries its own UI config; Depth/Margin reuse the units descriptor shape.
 */
/**
 * Hardware (vasalat-választó): a `/hardware` listából a `kind`-ra szűrve kínál vasalatokat. Az érték
 * objektum — a `fields` kulcsai alá a választott vasalat UUID-ja, a `constants` pedig változatlanul —,
 * és pontosan az az alak, amit a Unity a paraméter property-je alatt vár.
 */
export interface IHardwareParamTypeDescriptor extends IParamTypeDescriptor {
    /** Melyik vasalat-fajta kerüljön a legördülőbe (`uc_hardware.kind`). */
    kind: string;
    /**
     * Melyik kimeneti kulcsba a család MELYIK oldala kerüljön — pl.
     * `{"HardwareProduct":"Right","MirrorProduct":"Left"}`. Üres oldal = a család egyetlen tagja.
     * A választó EGY legördülő marad: a felhasználó a családot választja, az oldalakat ez bontja szét.
     */
    sides: { [outputKey: string]: string };
    /** Változatlanul a kimeneti objektumba másolt mezők (pl. a függesztő `BackMargin`-ja). */
    constants?: { [field: string]: string | number };
    /**
     * A „nincs vasalat" opció CÍMKÉJE (pl. „Függesztő nélküli"). Ha meg van adva, a legördülő ELSŐ
     * eleme ez, és a választásra ÜRES objektum tárolódik — amit a generateStyleScript nem küld ki.
     * Így a be/ki kapcsoló és a típusválasztó EGY mező, nem kettő.
     */
    none?: string;
}


export interface IDrawerSystemParamTypeDescriptor extends IParamTypeDescriptor {
    none?: string;
}

export interface IBackPassingParamTypeDescriptor extends IParamTypeDescriptor {
    relation: { label?: string; default?: string; options: { [name: string]: string } };
    depth: IBackPassingSizeField;
    margin: IBackPassingSizeField;
    /** Nút / falc szélessége (Unity: ChannelWidth). Hiányában a kártya és a tárolt érték nem ismeri. */
    channelWidth?: IBackPassingSizeField;
    top: {
        label?: string;
        default?: boolean;
        /** Mely kötéseknél jelenik meg a kapcsoló. Hiányában mindig látszik. */
        visibleForRelations?: string[];
        /**
         * Ha egy MÁSIK paraméter ezt az állapotot veszi fel (pl. be van kapcsolva a merevítő), a Top
         * kikapcsoltnak számít a kimenetben, és a kapcsoló nem látszik. Alakja a `visibleWhen`-é.
         */
        offWhen?: TParamVisibility | TParamVisibility[];
    };
    /**
     * ASZIMMETRIKUS HÁTLAP: melyik oldal LÁTSZIK a kész bútoron. A látható oldal falcolt, a többi
     * ráfekvő kötést kap — a választás a `relations.back.byVisibleSide` táblán át lesz kötés-iránnyá.
     * Ez a blokk CSAK a UI-é (címke, opciók, melyik kötésnél látszik); a descriptorból hiányozva a
     * kártya a mai négy mezőt mutatja, és a tárolt értékbe sem kerül `VisibleSide`.
     */
    visibleSide?: {
        label?: string;
        default?: string;
        /** Mely kötéseknél van értelme (és jelenik meg). Hiányában mindig látszik. */
        visibleForRelations?: string[];
        options: { [name: string]: string };
    };
    relations?: IRelationsDescriptor;
}

/**
 * Railing (Merevítő léc): composite object value {RailingType, RailingWidth} — the CabinetFrame's
 * back-bracing rail setup (kitchen base cabinets only). Unlike BackPassing, this is NOT mapped to a
 * Relations entry; `flatten: true` tells ParametersProcessor.generateStyleScript to spread the object's
 * fields directly onto the wrapper root, so Unity receives {"TargetType":"CabinetFrame","RailingType":
 * "Double","RailingWidth":"100mm"} rather than a nested {"Railing":{...}}.
 */
export interface IRailingParamTypeDescriptor extends IParamTypeDescriptor {
    /** A be/ki checkbox címkéje. A be/ki ÉRTÉKEK a railingType `offValue`/`onValue`-jából jönnek. */
    enabled?: { label?: string };
    railingType: {
        label?: string;
        default?: string;
        /**
         * A kikapcsolt checkbox implicit értéke (nem választható opció; a Unity RailingType enumja
         * szerint "Fill" = teljes kitöltés). Ebből derül ki a checkbox állapota is: bekapcsolva
         * akkor van, ha az érték ettől különbözik. Hiányában a kártya permisszív (bekapcsolt).
         */
        offValue?: string;
        /** Amire bekapcsoláskor váltunk (a leggyakoribb eset, pl. "Single"). */
        onValue?: string;
        options: { [name: string]: string };
    };
    railingWidth: {
        label?: string;
        default?: string;
        /** Mely railing-típusoknál értelmes (és jelenik meg) a szélesség. Hiányában mindig látszik. */
        visibleForTypes?: string[];
        units: IUnitsTypeDescriptor;
    };
    flatten?: boolean;
}

export default IParamTypeDescriptor;
